import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePriceGroup } from 'core/contexts/priceGroup';
import { Field, Form, FormSpy } from 'react-final-form';
import { TextInput } from '@utriper/core-ui';
import { Box, FormHelperText, Grid2 } from '@mui/material';
import Title from 'modules/Pricing/components/Title';
import PriceGroupGrid from 'modules/Pricing/components/PriceGroupGrid';
import ActionButtons from '../ActionButtons';

const AddRangeForm = ({ priceGroupId, handleClose = () => {} }) => {
    const { t } = useTranslation();
    const translate = (key) => t(`price_group_id.add_range_form.${key}`);
    const { addRange, submittingPriceGroup, unitsPriceGroup, bookingOrigins, prices: rangePrices, validatePrices } = usePriceGroup();

    const [submitted, setSubmitted] = useState(false);
    const [prices, setPrices] = useState([]);
    const [quantityRange, setQuantityRange] = useState(null);

    const lastQuantityTo = useMemo(() => rangePrices?.sort((a, b) => a?.quantityTo - b?.quantityTo)?.[rangePrices?.length - 1]?.quantityTo, [rangePrices]);

    useEffect(() => {
        if (!quantityRange) return;

        if (Array.isArray(unitsPriceGroup) && unitsPriceGroup?.length > 0) {
            const rangePrice = {
                rowId: 0,
                quantityFrom: quantityRange?.quantityFrom,
                quantityTo: quantityRange?.quantityTo,
                prices: [],
            };

            for (const unit of unitsPriceGroup ?? []) {
                rangePrice.prices.push({
                    unitId: unit?.id,
                    price: 0,
                    bookingOriginId: null,
                });

                if (Array.isArray(bookingOrigins) && bookingOrigins?.length > 0) {
                    for (const bookingOriginId of bookingOrigins ?? []) {
                        rangePrice.prices.push({
                            unitId: unit?.id,
                            price: 0,
                            bookingOriginId,
                        });
                    }
                }
            }

            setPrices([rangePrice]);
        }
    }, [quantityRange, rangePrices, unitsPriceGroup, bookingOrigins]);

    const sanitizeNumberString = (str) => {
        if (str === undefined || str === null) return '';
        const cleaned = String(str).replace(/[^0-9]/g, '');
        return cleaned === '' ? '' : String(parseInt(cleaned, 10));
    };

    const handleSubmit = async () => {
        await addRange(priceGroupId, prices);
        handleClose();
    };

    const validateForm = ({ quantityFrom, quantityTo }) => {
        const errors = {};

        if (!Number.isFinite(quantityFrom)) {
            errors.quantityFrom = translate('validate_form.required');
        }

        if (quantityFrom !== lastQuantityTo + 1) {
            errors.quantityFrom = `${translate('validate_form.quantity_from')} ${lastQuantityTo + 1}!`;
        }

        if (quantityTo && !Number.isFinite(quantityTo)) {
            errors.quantityTo = translate('validate_form.invalid_quantity_to');
        }

        if (quantityTo && quantityTo <= lastQuantityTo) {
            errors.quantityTo = `${translate('validate_form.minor_quantity_to')} ${lastQuantityTo}!`;
        }

        const priceErrors = validatePrices(prices, unitsPriceGroup);

        errors.prices = priceErrors;

        return errors;
    };

    const handleChangeForm = ({ values }) => {
        const { quantityFrom, quantityTo } = values;
        if (!Number.isFinite(quantityFrom)) return;

        setQuantityRange({
            quantityFrom,
            quantityTo,
        });
    };

    return (
        <Form
            key={`price-group-form-units-${priceGroupId}`}
            onSubmit={handleSubmit}
            validate={validateForm}
            render={({ errors, handleSubmit, form }) => {
                const hasPriceRangesErrors = submitted && errors?.prices;
                const priceRangesErrors = errors?.prices ?? [];

                return (
                    <form key={`price-group-form-units-${priceGroupId}`} onSubmit={handleSubmit} noValidate>
                        <Box component="section" py={2}>
                            <Title title={translate('title')} description={translate('description')} />

                            {/* Form */}
                            <Grid2 container direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px">
                                <Grid2 container size={12} spacing={1} alignItems="center">
                                    {/* Quantity From */}
                                    <Grid2 size={6}>
                                        <Field name={'quantityFrom'}>
                                            {({ input, meta }) => (
                                                <TextInput
                                                    label={translate('form.quantity_from')}
                                                    meta={meta}
                                                    value={input?.value}
                                                    onChange={(event) => {
                                                        const cleaned = sanitizeNumberString(event?.target?.value);
                                                        event.target.value = cleaned;
                                                        input.onChange(cleaned === '' ? '' : Number(cleaned));
                                                    }}
                                                    input={{
                                                        fullWidth: true,
                                                        type: 'text',
                                                        inputMode: 'numeric',
                                                        slotProps: {
                                                            inputProps: { inputMode: 'numeric', pattern: '[0-9]*', min: 1 },
                                                        },
                                                        error: meta?.touched && Boolean(meta?.error),
                                                        helperText: meta?.touched && meta?.error ? meta.error : '',
                                                    }}
                                                />
                                            )}
                                        </Field>
                                    </Grid2>

                                    {/* Quantity To */}
                                    <Grid2 size={6}>
                                        <Field name={'quantityTo'}>
                                            {({ input, meta }) => (
                                                <TextInput
                                                    label={translate('form.quantity_to')}
                                                    meta={meta}
                                                    value={input?.value}
                                                    onChange={(event) => {
                                                        const raw = event?.target?.value;
                                                        if (raw === '') {
                                                            input.onChange('');
                                                            return;
                                                        }
                                                        const cleaned = sanitizeNumberString(raw);
                                                        event.target.value = cleaned;
                                                        input.onChange(cleaned === '' ? '' : Number(cleaned));
                                                    }}
                                                    input={{
                                                        fullWidth: true,
                                                        type: 'text',
                                                        inputMode: 'numeric',
                                                        slotProps: {
                                                            inputProps: { inputMode: 'numeric', pattern: '[0-9]*', min: 1 },
                                                        },
                                                        error: meta?.touched && Boolean(meta?.error),
                                                        helperText: meta?.touched && meta?.error ? meta.error : '',
                                                    }}
                                                />
                                            )}
                                        </Field>
                                    </Grid2>
                                </Grid2>

                                {prices?.length > 0 && quantityRange?.quantityFrom && (
                                    <Box width="100%" maxHeight="640px" overflow="auto" mt={1}>
                                        <PriceGroupGrid
                                            data={{
                                                units: unitsPriceGroup,
                                                bookingOrigins,
                                                prices,
                                            }}
                                            actions={{
                                                setPrices,
                                            }}
                                        />
                                    </Box>
                                )}

                                {/* Errors Ranges */}
                                <Box
                                    sx={{
                                        width: '100%',
                                        maxHeight: '78px',
                                        overflowY: 'auto',
                                        mt: 2,
                                    }}
                                >
                                    {hasPriceRangesErrors && priceRangesErrors?.length > 0 && priceRangesErrors.map((error) => <FormHelperText error={hasPriceRangesErrors}>{error}</FormHelperText>)}
                                </Box>
                            </Grid2>

                            <ActionButtons
                                handleSubmit={(e) => {
                                    setSubmitted(true);
                                    return handleSubmit(e);
                                }}
                                handleClose={(e) => {
                                    handleClose();
                                    return form.reset(e);
                                }}
                                submitting={submittingPriceGroup}
                                labelNext={translate('add_button')}
                            />
                        </Box>

                        {/* Form Spy */}
                        <FormSpy subscription={{ values: true }} onChange={handleChangeForm} />
                    </form>
                );
            }}
        />
    );
};

export default AddRangeForm;
